import { useEffect, useRef } from "react";
import greekFlag from "@/assets/icons/flag-el.svg";
import englishFlag from "@/assets/icons/flag-en.svg";
import { SITE_ROUTES } from "@/config/siteRoutes";
import { GREEK_LANGUAGE_SWITCH_ENABLED } from "@/config/siteLanguages";
import { useSiteBrand } from "@/data/siteBrand";
import { getLanguageSwitchHref, getSiteLocale, localizeHref, stripLocalePrefix } from "@/i18n/locale";
import { localizeContent, uiText } from "@/i18n/translations";
import {
  HOME_HERO_URL,
  handleHomeHeroClick,
} from "@/utils/homeNavigation";

const homeMenuItems = [
  { id: "hero", label: "Home", meta: "Digital-first presence" },
  { id: "identity", label: "Identity", meta: "Brand language" },
  { id: "motion-system", label: "Motion", meta: "Scroll-led transition" },
  { id: "services", label: "Services", meta: "Web agency first" },
] as const;

type SiteMenuLinkItem = {
  href: string;
  label: string;
  meta: string;
};

type SiteMenuSectionItem = {
  id: string;
  label: string;
  meta: string;
};

type SiteMenuOverlayProps =
  | {
      mode?: "sections";
      activeSectionId: string;
      currentPath?: never;
      items?: readonly SiteMenuSectionItem[];
      open: boolean;
      onClose: () => void;
      onNavigate: (sectionId: string) => void;
    }
  | {
      mode: "links";
      activeSectionId?: never;
      currentPath: string;
      items: readonly SiteMenuLinkItem[];
      open: boolean;
      onClose?: () => void;
      onNavigate?: never;
    };

// Destructuring a discriminated union at the parameter list breaks TypeScript's
// narrowing — each field's type collapses to the union of every member's type,
// so `mode === "links"` downstream can't tell the compiler that `items` is then
// definitely SiteMenuLinkItem[]. Keeping `props` as one value and narrowing on
// `props.mode` (not a destructured local) is what lets TS actually track it.
export function SiteMenuOverlay(props: SiteMenuOverlayProps) {
  const { open } = props;
  const brand = useSiteBrand();
  const locale = getSiteLocale();
  const alternateLanguage = locale === "el"
    ? { code: "en" as const, label: "English", flag: englishFlag }
    : { code: "el" as const, label: "Ελληνικά", flag: greekFlag };
  const overlay = useRef<HTMLElement>(null);
  const onClose = props.onClose;

  useEffect(() => {
    if (!open) return;
    const element = overlay.current;
    const toggle = element?.closest('.experienceStage')?.querySelector<HTMLElement>('.identity-chrome-menu');
    const content = [...document.querySelectorAll<HTMLElement>('main, [data-site-footer]')]
      .filter((item) => !item.contains(element));
    const previousInert = content.map((item) => item.inert);
    content.forEach((item) => { item.inert = true; });
    element?.querySelector<HTMLElement>('.site-menu-link')?.focus();
    const keydown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') { event.preventDefault(); onClose?.(); }
      if (event.key !== 'Tab') return;
      const targets = [toggle, ...element?.querySelectorAll<HTMLElement>('.site-menu-brand, .site-menu-link, .site-menu-language-link') ?? []]
        .filter((item): item is HTMLElement => !!item);
      const index = targets.indexOf(document.activeElement as HTMLElement);
      event.preventDefault();
      targets[(index + (event.shiftKey ? -1 : 1) + targets.length) % targets.length]?.focus();
    };
    document.addEventListener('keydown', keydown);
    return () => {
      content.forEach((item, index) => { item.inert = previousInert[index]; });
      document.removeEventListener('keydown', keydown);
      toggle?.focus();
    };
  }, [open, onClose]);
  const handleLinkClick = (href: string, event?: { preventDefault: () => void }) => {
    if (stripLocalePrefix(href) === SITE_ROUTES.home) {
      handleHomeHeroClick(event);
    }

    props.onClose?.();
  };

  const handleSectionNavigate = (sectionId: string) => {
    if (props.mode === "links") return;
    props.onClose();
    const navigate = props.onNavigate;
    window.requestAnimationFrame(() => navigate(sectionId));
  };

  return (
    <aside
      ref={overlay}
      id="siteMenuOverlay"
      className="site-menu-overlay"
      data-open={open}
      aria-hidden={!open}
      inert={!open}
      role="dialog"
      aria-modal={open || undefined}
      aria-label={uiText("Site navigation", locale)}
    >
      <div className="site-menu-shell">
        <div className="site-menu-visual" data-chrome-surface="dark">
          <a
            className="site-menu-brand"
            href={HOME_HERO_URL}
            aria-label={`${brand.brand_name} ${locale === "el" ? "αρχική" : "home"}`}
            onClick={handleHomeHeroClick}
          >
            <img src={brand.logo_url} alt={brand.brand_name} />
          </a>

          <div className="site-menu-visual-stack">
            <div
              className="site-menu-visual-main surface-frame"
              style={{
                backgroundImage: `url('${brand.menu_visual_url}')`,
                backgroundPosition: brand.menu_visual_position,
              }}
            />
          </div>

          <div className="site-menu-visual-words" aria-hidden="true">
            <span className="site-menu-visual-word site-menu-visual-word--one">
              {brand.menu_words[0]}
            </span>
            <span className="site-menu-visual-word site-menu-visual-word--two">
              {brand.menu_words[1]}
            </span>
            <span className="site-menu-visual-word site-menu-visual-word--three">
              {brand.menu_words[2]}
            </span>
          </div>
        </div>

        <div className="site-menu-copy" data-chrome-surface="light">
          <div className="site-menu-copy-top">
            <span className="site-menu-kicker">{brand.menu_kicker}</span>
          </div>

          <nav className="site-menu-nav" aria-label={uiText("Primary", locale)}>
            {props.mode === "links"
              ? (brand.primary_navigation.length > 0
                  ? brand.primary_navigation
                  : props.items ?? []
                ).map((item) => {
                  const localizedItem = localizeContent(item, locale);
                  return (
                  <a
                    key={localizedItem.href}
                    href={stripLocalePrefix(localizedItem.href) === SITE_ROUTES.home ? HOME_HERO_URL : localizeHref(localizedItem.href, locale)}
                    className="site-menu-link"
                    data-active={stripLocalePrefix(props.currentPath) === stripLocalePrefix(localizedItem.href)}
                    data-magnetic
                    onClick={(event) => handleLinkClick(localizedItem.href, event)}
                  >
                    <span className="site-menu-link-label">{localizedItem.label}</span>
                    <span className="site-menu-link-meta">{localizedItem.meta}</span>
                  </a>
                  );
                })
              : localizeContent(props.items ?? homeMenuItems, locale).map((item) => (
                  <button
                    key={item.id}
                    type="button"
                    className="site-menu-link"
                    data-active={props.activeSectionId === item.id}
                    data-magnetic
                    onClick={() => handleSectionNavigate(item.id)}
                  >
                    <span className="site-menu-link-label">{item.label}</span>
                    <span className="site-menu-link-meta">{item.meta}</span>
                  </button>
                ))}
          </nav>

          <div className="site-menu-footer">
            {(GREEK_LANGUAGE_SWITCH_ENABLED || locale === "el") && <nav className="site-menu-languages" aria-label={uiText("Language", locale)}>
              <a
                className="site-menu-language-link"
                href={getLanguageSwitchHref()}
                hrefLang={alternateLanguage.code}
                lang={alternateLanguage.code}
                aria-label={uiText(alternateLanguage.code === "el" ? "Switch to Greek" : "Switch to English", locale)}
              >
                <img src={alternateLanguage.flag} alt="" aria-hidden="true" />
                <span>{alternateLanguage.label}</span>
              </a>
            </nav>}
            <span className="site-menu-footer-mark" aria-hidden="true">
              <img src={brand.logo_url} alt="" />
            </span>
          </div>
        </div>
      </div>
    </aside>
  );
}
